import { UserRole, FULL_ADMIN_ROLES, ADMIN_UI_ROLES, ALL_AUTHENTICATED_ROLES } from '../../../core/models/role.enum';

export interface ChildMenuItem {
  label: string;
  route: string;
  icon?: string;
  /** Only exact URL match activates this item — used for "Overview" children that share the parent route */
  exactMatch?: boolean;
  /** Extra query params to pass along with the routerLink navigation (e.g. { tab: 'users' }) */
  queryParams?: Record<string, string>;
  /**
   * Substring to test the current URL against for the active/highlighted state.
   * Needed when the link's `route` doesn't reflect the final resolved URL
   * (e.g. `/organization?tab=users` redirects to `/organization/:slug/users`).
   */
  activeMatchSegment?: string;
  /** Only show this child if user has one of these roles (Full Admin bypasses this unless explicitly in hideForRoles) */
  roles?: UserRole[];
  /** Hide this child for specific roles */
  hideForRoles?: UserRole[];
  /** Only show this child if the user has any of these privileges */
  privileges?: string[];
  /** When set, the child renders as a button that triggers this action instead of navigating */
  action?: 'order-booking';
}

export interface MenuItem {
  label: string;
  route: string;
  icon: string;
  group: string;
  /** Only show this item if user has one of these roles (Full Admin bypasses this unless explicitly in hideForRoles) */
  roles?: UserRole[];
  /** Hide this item for specific roles */
  hideForRoles?: UserRole[];
  /** Only show this item if the user has any of these privileges */
  privileges?: string[];
  /** Child sub-menu items */
  children?: ChildMenuItem[];
}

export interface MenuGroup {
  name: string;
  items: MenuItem[];
}

/**
 * Roles that have restricted, dedicated sidebar access.
 */
export const PHARMACIST_ROLES: UserRole[] = [UserRole.PHARMACIST];
export const TELECALLER_ROLES: UserRole[] = [UserRole.TELECALLER];
export const SALES_EXECUTIVE_ROLES: UserRole[] = [
  UserRole.SALES_EXECUTIVE_MR,
  UserRole.SALES_EXECUTIVE,
  UserRole.SALES_EXECUTIVE_LOWER,
  UserRole.REPRESENTATIVE,
];

/**
 * Master sidebar menu item definitions.
 */
export const SIDEBAR_MENU_ITEMS: MenuItem[] = [
  // ── OVERVIEW ────────────────────────────────────────────────────
  {
    label: 'Dashboard',
    route: '/dashboard',
    icon: 'dashboard',
    privileges: ['view_dashboard'],
    hideForRoles: [UserRole.PHARMACIST],
    group: 'OVERVIEW',
  },
  {
    label: 'Reports',
    route: '/reports',
    icon: 'chart-bar',
    privileges: ['view_dashboard', 'view_reports'],
    hideForRoles: [UserRole.TELECALLER, UserRole.PHARMACIST, ...SALES_EXECUTIVE_ROLES],
    group: 'OVERVIEW',
  },

  // ── PATIENTS ────────────────────────────────────────────────────
  {
    label: 'Patient List',
    route: '/patient',
    icon: 'users',
    privileges: ['view_patients'],
    hideForRoles: [UserRole.TELECALLER],
    group: 'PATIENTS',
  },
  {
    label: 'Patient List',
    route: '/patient-list',
    icon: 'users',
    privileges: ['view_assigned_patients', 'view_patients'],
    hideForRoles: ADMIN_UI_ROLES,
    roles: [UserRole.TELECALLER],
    group: 'PATIENTS',
  },

  // ── CLINICAL ────────────────────────────────────────────────────
  {
    label: 'Doctor List',
    route: '/doctors',
    icon: 'doctor',
    privileges: ['view_doctors'],
    hideForRoles: [UserRole.TELECALLER, UserRole.PHARMACIST],
    group: 'CLINICAL',
  },
  {
    label: 'Hospitals',
    route: '/hospitals',
    icon: 'hospital',
    privileges: ['view_hospitals'],
    hideForRoles: [UserRole.TELECALLER, UserRole.PHARMACIST, ...SALES_EXECUTIVE_ROLES],
    group: 'CLINICAL',
  },
  {
    label: 'Labs',
    route: '/labs',
    icon: 'labs',
    privileges: ['view_labs'],
    hideForRoles: [
      ...SALES_EXECUTIVE_ROLES,
      UserRole.TELECALLER,
      UserRole.PHARMACIST,
    ],
    group: 'CLINICAL',
    children: [
      { label: 'Lab List', route: '/labs', icon: 'labs' },
      { label: 'Lab Test Bookings', route: '/lab-test-bookings', icon: 'flask' },
      { label: 'Lab Tests', route: '/lab-tests', icon: 'flask' },
    ],
  },

  // ── OPERATIONS ──────────────────────────────────────────────────
  {
    label: 'Telecaller Mgmt',
    route: '/telecaller',
    icon: 'telecaller',
    privileges: ['view_users', 'assign_telecallers', 'create_telecallers'],
    hideForRoles: [UserRole.TELECALLER, UserRole.PHARMACIST, ...SALES_EXECUTIVE_ROLES],
    group: 'OPERATIONS',
  },
  {
    label: 'Call Logs',
    route: '/call-logs',
    icon: 'call',
    roles: [UserRole.TELECALLER],
    privileges: ['view_followups', 'log_followups'],
    hideForRoles: [UserRole.PHARMACIST, ...SALES_EXECUTIVE_ROLES],
    group: 'OPERATIONS',
  },

  // ── COMMERCE ────────────────────────────────────────────────────
  {
    label: 'Inventory',
    route: '/inventory',
    icon: 'inventory',
    privileges: [
      'view_medicines',
      'view_suppliers',
      'view_batches',
      'view_special_pricing',
      'add_stock',
      'deduct_stock',
      'adjust_stock',
      'view_inventory_dashboard',
    ],
    hideForRoles: [UserRole.TELECALLER, ...SALES_EXECUTIVE_ROLES],
    group: 'COMMERCE',
    children: [
      { label: 'Command Center', route: '/inventory', icon: 'dashboard', exactMatch: true },
      { label: 'Stock Ledger', route: '/inventory/stock', icon: 'inventory' },
      { label: 'Medicines', route: '/inventory/medicines', icon: 'labs' },
      { label: 'Batches', route: '/inventory/batches', icon: 'bookmark' },
      { label: 'Suppliers', route: '/inventory/suppliers', icon: 'briefcase', hideForRoles: [UserRole.PHARMACIST] },
      { label: 'Stock Alerts', route: '/inventory/alerts', icon: 'alert-circle' },
      {
        label: 'Coupons',
        route: '/inventory/coupons',
        icon: 'bookmark',
        privileges: ['view_coupons', 'edit_coupons', 'create_coupons', 'delete_coupons'],
      },
      {
        label: 'Special Pricing',
        route: '/special-pricing',
        icon: 'tag',
        privileges: [
          'view_pricing',
          'view_special_pricing',
          'manage_pricing',
          'delete_special_pricing',
          'edit_special_pricing',
          'create_special_pricing',
        ],
        hideForRoles: [UserRole.PHARMACIST],
      },
    ],
  },
  {
    label: 'Orders',
    route: '/orders',
    icon: 'cart',
    privileges: [
      'view_orders',
      'create_orders',
      'edit_orders',
      'confirm_orders',
      'update_order_delivery',
      'cancel_orders',
      'view_lab_bookings',
      'create_lab_bookings',
    ],
    hideForRoles: [UserRole.TELECALLER],
    group: 'COMMERCE',
    children: [
      { label: 'Overview', route: '/orders', icon: 'dashboard', exactMatch: true },
      {
        label: 'Medicine Orders',
        route: '/orders/medicine-orders',
        icon: 'labs',
        hideForRoles: [...SALES_EXECUTIVE_ROLES],
      },
      {
        label: 'Book Medicine',
        route: '/orders/medicine-orders/create',
        icon: 'cart',
        action: 'order-booking',
        privileges: ['create_orders'],
        hideForRoles: [...SALES_EXECUTIVE_ROLES],
      },
      {
        label: 'Lab Test Booking',
        route: '/orders/lab-test-orders',
        icon: 'flask',
        hideForRoles: [UserRole.PHARMACIST],
      },
    ],
  },

  // ── ADMIN ───────────────────────────────────────────────────────
  {
    label: 'Organization',
    route: '/organization',
    icon: 'briefcase',
    privileges: ['update_organization', 'view_roles', 'view_users'],
    hideForRoles: [UserRole.TELECALLER, UserRole.PHARMACIST, ...SALES_EXECUTIVE_ROLES],
    group: 'ADMIN',
    children: [
      { label: 'Users', route: '/organization', icon: 'users', queryParams: { tab: 'users' }, activeMatchSegment: '/users' },
      { label: 'Roles', route: '/organization', icon: 'userCheck', queryParams: { tab: 'roles' }, activeMatchSegment: '/roles' },
      { label: 'Programs', route: '/organization', icon: 'bookmark', queryParams: { tab: 'programs' }, activeMatchSegment: '/programs' },
      { label: 'Zones', route: '/organization', icon: 'map', queryParams: { tab: 'zones' }, activeMatchSegment: '/zones' },
      { label: 'HQ', route: '/organization', icon: 'building', queryParams: { tab: 'hq' }, activeMatchSegment: '/hq' },
    ],
  },
  {
    label: 'Audit Trail',
    route: '/audit-trail',
    icon: 'bookmark',
    privileges: [],
    roles: [UserRole.SUPER_ADMIN],
    hideForRoles: ALL_AUTHENTICATED_ROLES.filter((r) => r !== UserRole.SUPER_ADMIN),
    group: 'ADMIN',
  },
];

/**
 * Checks if a user's role matches any of the target roles (case-insensitive fallback included).
 */
export function isUserInRole(userRole: UserRole | string | undefined | null, targetRoles?: UserRole[]): boolean {
  if (!userRole || !targetRoles || !targetRoles.length) return false;
  const userRoleStr = userRole.toString().toLowerCase();
  return targetRoles.some((r) => {
    const rStr = r.toString().toLowerCase();
    return r === userRole || rStr === userRoleStr || userRoleStr.includes(rStr);
  });
}

/**
 * Determines whether a top-level menu item should be visible for the given user and privileges.
 */
export function isMenuItemVisible(
  item: MenuItem,
  user: { role?: UserRole | string } | null,
  hasAnyPrivilege: (privileges: string[]) => boolean
): boolean {
  if (!user) return false;

  const isFullAdmin = isUserInRole(user.role, FULL_ADMIN_ROLES);

  // 1. If explicitly hidden for this role, do not show
  if (item.hideForRoles && isUserInRole(user.role, item.hideForRoles)) {
    return false;
  }

  // 2. Full Admin bypasses positive role/privilege constraints
  if (isFullAdmin) {
    return true;
  }

  // 3. If explicitly permitted for this role, allow access
  if (item.roles && isUserInRole(user.role, item.roles)) {
    return true;
  }

  // 4. If privileges are specified, check if user has any of them
  if (item.privileges && item.privileges.length > 0) {
    return hasAnyPrivilege(item.privileges);
  }

  // 5. If item was restricted by roles and user didn't match, deny
  if (item.roles && item.roles.length > 0) {
    return false;
  }

  return true;
}

/**
 * Determines whether a child sub-menu item should be visible for the given user and privileges.
 */
export function isChildMenuItemVisible(
  child: ChildMenuItem,
  user: { role?: UserRole | string } | null,
  hasAnyPrivilege: (privileges: string[]) => boolean
): boolean {
  if (!user) return false;

  const isFullAdmin = isUserInRole(user.role, FULL_ADMIN_ROLES);

  // 1. If explicitly hidden for this role, do not show
  if (child.hideForRoles && isUserInRole(user.role, child.hideForRoles)) {
    return false;
  }

  // 2. Full Admin bypasses positive constraints
  if (isFullAdmin) {
    return true;
  }

  // 3. If explicitly permitted for this role, allow access
  if (child.roles && isUserInRole(user.role, child.roles)) {
    return true;
  }

  // 4. If privileges are specified, check if user has any
  if (child.privileges && child.privileges.length > 0) {
    return hasAnyPrivilege(child.privileges);
  }

  // 5. If child was restricted by roles and user didn't match, deny
  if (child.roles && child.roles.length > 0) {
    return false;
  }

  return true;
}

/**
 * Filters and groups menu items for rendering in the sidebar.
 */
export function getFilteredGroupedMenuItems(
  menuItems: MenuItem[],
  user: { role?: UserRole | string } | null,
  hasAnyPrivilege: (privileges: string[]) => boolean
): MenuGroup[] {
  const visibleItems = menuItems.filter((item) => isMenuItemVisible(item, user, hasAnyPrivilege));

  const groupOrder: string[] = [];
  const groupMap = new Map<string, MenuItem[]>();

  for (const item of visibleItems) {
    if (!groupMap.has(item.group)) {
      groupMap.set(item.group, []);
      groupOrder.push(item.group);
    }
    groupMap.get(item.group)!.push(item);
  }

  return groupOrder.map((name) => ({
    name,
    items: groupMap.get(name)!,
  }));
}
